<template>
  <div class="cws-container blog-wrapper">
    <div>
      <b-row>
        <b-col md="12" lg="12">
          <b-card
              title="SIQ Анализ"
              class="mb-3"
              border-variant="primary"
          >
            <b-card-text>
              Загрузка SIQ-файла для анализа пакета
            </b-card-text>

            <b-form-group
                label="SIQ-Файл"
                label-for="siq-file"
            >
              <b-button
                  variant="outline-primary"
                  @click="$refs.refInputUploadFile.click()"
              >
                <input
                    ref="refInputUploadFile"
                    type="file"
                    class="d-none"
                    id="siq-file"
                    @input="inputSiqFileRenderer"
                />
                <span class="d-none d-sm-inline">Upload</span>
                <feather-icon
                    icon="UploadIcon"
                    class="d-inline d-sm-none"
                />
              </b-button>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- content -->
    <div class="blog-detail-wrapper">
      <b-row v-if="analysisData">
        <b-col cols="12">
          <b-overlay
              :show="isLoading"
              rounded="sm"
          >
            <b-card>
              <h2 class="mb-1">{{ analysisData.title }}</h2>
              <p class="text-muted mb-2">
                Автор: <strong>{{ analysisData.author }}</strong> |
                Всего песен: <strong>{{ analysisData.total_songs }}</strong>
                <template v-if="analysisData.stats">
                  | Из БД: <strong>{{ analysisData.stats.resolved_from_db }}</strong>,
                  через ЛЛМ: <strong>{{ analysisData.stats.sent_to_llm }}</strong>
                </template>
              </p>

              <b-alert
                  variant="warning"
                  :show="artistDuplicates.length > 0"
                  class="mb-2"
              >
                <div class="alert-body">
                  <h5 class="alert-heading mb-1">
                    <feather-icon icon="AlertTriangleIcon" class="mr-50" />
                    Возможные повторы артистов: {{ artistDuplicates.length }}
                  </h5>
                  <div
                      v-for="(cluster, cIdx) in artistDuplicates"
                      :key="'dup-' + cIdx"
                      class="mb-1"
                  >
                    <strong>{{ cluster.names.join(' / ') }}</strong>
                    <span class="text-muted"> — в {{ cluster.questions_count }} вопросах:</span>
                    <ul class="mb-0 mt-25">
                      <li
                          v-for="(occ, oIdx) in cluster.occurrences"
                          :key="'dup-' + cIdx + '-occ-' + oIdx"
                      >
                        Раунд {{ occ.round_index + 1 }} «{{ occ.round_name }}» →
                        Тема {{ occ.theme_index + 1 }} «{{ occ.theme_name }}» →
                        вопрос {{ occ.question_in_theme }}<template v-if="occ.price != null"> ({{ occ.price }} очков)</template>:
                        {{ songLabel(occ) }}<template v-if="occ.letter"> (вариант {{ occ.letter }})</template><template v-if="occ.is_feat"> (feat)</template>
                      </li>
                    </ul>
                  </div>
                </div>
              </b-alert>

              <b-tabs content-class="mt-2" fill>
                <!-- Таб: Общая информация -->
                <b-tab title="Общая информация" active>
                  <b-row class="mt-2">
                    <!-- Русские / Иностранные -->
                    <b-col md="4" cols="12">
                      <b-card title="По стране исполнителя" class="mb-2">
                        <vue-apex-charts
                            type="donut"
                            height="300"
                            :options="artistsCountryChartOptions"
                            :series="artistsCountrySeries"
                        />
                        <b-row class="mt-2 songs-lists">
                          <b-col cols="6">
                            <h6 class="text-russian mb-1">Русские ({{ russianSongs.length }})</h6>
                            <div class="songs-scroll">
                              <div
                                  v-for="song in russianSongs"
                                  :key="'ru-' + song.index"
                                  class="song-item text-russian"
                              >
                                <small>{{ songLabel(song) }}</small>
                                <b-badge
                                    v-if="llmFieldsLabel(song)"
                                    variant="light-warning"
                                    class="ml-25"
                                    :title="'Через ЛЛМ: ' + llmFieldsLabel(song)"
                                >ЛЛМ</b-badge>
                              </div>
                            </div>
                          </b-col>
                          <b-col cols="6">
                            <h6 class="text-foreign mb-1">Иностранные ({{ foreignSongs.length }})</h6>
                            <div class="songs-scroll">
                              <div
                                  v-for="song in foreignSongs"
                                  :key="'foreign-' + song.index"
                                  class="song-item text-foreign"
                              >
                                <small>{{ songLabel(song) }}</small>
                                <b-badge
                                    v-if="llmFieldsLabel(song)"
                                    variant="light-warning"
                                    class="ml-25"
                                    :title="'Через ЛЛМ: ' + llmFieldsLabel(song)"
                                >ЛЛМ</b-badge>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-card>
                    </b-col>

                    <!-- По жанру -->
                    <b-col md="4" cols="12">
                      <b-card title="По жанру" class="mb-2">
                        <vue-apex-charts
                            type="donut"
                            height="300"
                            :options="genreChartOptions"
                            :series="genreSeries"
                        />
                        <b-row class="mt-2 songs-lists">
                          <b-col
                              v-for="(songs, genre) in songsByGenre"
                              :key="'genre-list-' + genre"
                              cols="6"
                          >
                            <h6 class="mb-1" :style="{color: getGenreColor(genre)}">{{ genre }} ({{ songs.length }})</h6>
                            <div class="songs-scroll">
                              <div
                                  v-for="song in songs"
                                  :key="'g-' + genre + '-' + song.index"
                                  class="song-item"
                                  :style="{color: getGenreColor(genre)}"
                              >
                                <small>{{ songLabel(song) }}</small>
                                <b-badge
                                    v-if="llmFieldsLabel(song)"
                                    variant="light-warning"
                                    class="ml-25"
                                    :title="'Через ЛЛМ: ' + llmFieldsLabel(song)"
                                >ЛЛМ</b-badge>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-card>
                    </b-col>

                    <!-- 20 век / 21 век -->
                    <b-col md="4" cols="12">
                      <b-card title="По эпохе" class="mb-2">
                        <vue-apex-charts
                            type="donut"
                            height="300"
                            :options="centuryChartOptions"
                            :series="centurySeries"
                        />
                      </b-card>
                    </b-col>
                  </b-row>

                  <!-- График по десятилетиям (количество) -->
                  <b-row class="mt-2">
                    <b-col cols="12">
                      <b-card title="По десятилетиям (количество)" class="mb-2">
                        <vue-apex-charts
                            type="bar"
                            height="350"
                            :options="decadesChartOptions"
                            :series="decadesSeries"
                        />
                        <b-row class="mt-2 songs-lists" v-if="sortedDecades.length > 0">
                          <b-col
                              v-for="decade in sortedDecades"
                              :key="'decade-' + decade"
                              :cols="12"
                              :md="Math.max(2, Math.floor(12 / sortedDecades.length))"
                          >
                            <h6 class="text-primary mb-1">{{ decade }}-е ({{ songsByDecade[decade].length }})</h6>
                            <div class="songs-scroll">
                              <div
                                  v-for="song in songsByDecade[decade]"
                                  :key="'d' + decade + '-' + song.index"
                                  class="song-item"
                              >
                                <small>{{ songLabel(song) }}</small>
                                <b-badge
                                    v-if="llmFieldsLabel(song)"
                                    variant="light-warning"
                                    class="ml-25"
                                    :title="'Через ЛЛМ: ' + llmFieldsLabel(song)"
                                >ЛЛМ</b-badge>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-card>
                    </b-col>
                  </b-row>

                  <!-- Графики по стоимости -->
                  <h4 class="mt-2 mb-1">По стоимости</h4>
                  <b-row>
                    <!-- По стране (стоимость) -->
                    <b-col md="4" cols="12">
                      <b-card title="По стране исполнителя (стоимость)" class="mb-2">
                        <vue-apex-charts
                            type="donut"
                            height="300"
                            :options="artistsCountryCostChartOptions"
                            :series="artistsCountryCostSeries"
                        />
                      </b-card>
                    </b-col>

                    <!-- По жанру (стоимость) -->
                    <b-col md="4" cols="12">
                      <b-card title="По жанру (стоимость)" class="mb-2">
                        <vue-apex-charts
                            type="donut"
                            height="300"
                            :options="genreCostChartOptions"
                            :series="genreCostSeries"
                        />
                      </b-card>
                    </b-col>

                    <!-- По эпохе (стоимость) -->
                    <b-col md="4" cols="12">
                      <b-card title="По эпохе (стоимость)" class="mb-2">
                        <vue-apex-charts
                            type="donut"
                            height="300"
                            :options="centuryCostChartOptions"
                            :series="centuryCostSeries"
                        />
                      </b-card>
                    </b-col>
                  </b-row>

                  <!-- По десятилетиям (стоимость) -->
                  <b-row class="mt-2">
                    <b-col cols="12">
                      <b-card title="По десятилетиям (стоимость)" class="mb-2">
                        <vue-apex-charts
                            type="bar"
                            height="350"
                            :options="decadesCostChartOptions"
                            :series="decadesCostSeries"
                        />
                      </b-card>
                    </b-col>
                  </b-row>
                </b-tab>

                <!-- Таб: Структура -->
                <b-tab title="Структура" v-if="analysisData.structure">
                  <!-- Переключатели параметров -->
                  <div class="d-flex align-items-center flex-wrap mb-1 mt-1">
                    <span class="mr-1 font-weight-bold">Раскраска:</span>
                    <b-button-group size="sm">
                      <b-button
                          :variant="structureColorMode === 'language' ? 'primary' : 'outline-primary'"
                          @click="structureColorMode = 'language'"
                      >
                        Язык
                      </b-button>
                      <b-button
                          :variant="structureColorMode === 'genre' ? 'primary' : 'outline-primary'"
                          @click="structureColorMode = 'genre'"
                      >
                        Жанр
                      </b-button>
                      <b-button
                          :variant="structureColorMode === 'decade' ? 'primary' : 'outline-primary'"
                          @click="structureColorMode = 'decade'"
                      >
                        Декада
                      </b-button>
                    </b-button-group>
                    <b-button
                        v-if="structureColorMode"
                        variant="flat-secondary"
                        size="sm"
                        class="ml-1"
                        @click="structureColorMode = null"
                    >
                      <feather-icon icon="XIcon" size="14" class="mr-25" />
                      Сбросить
                    </b-button>
                  </div>

                  <!-- Легенда -->
                  <div v-if="structureColorMode" class="structure-legend mb-2">
                    <div
                        v-for="item in currentLegend"
                        :key="'legend-' + item.label"
                        class="legend-item"
                    >
                      <span class="legend-color" :style="{backgroundColor: item.color}"></span>
                      <span class="legend-label">{{ item.label }}</span>
                    </div>
                  </div>

                  <!-- Таблица структуры -->
                  <div class="structure-table-wrapper">
                    <table class="structure-table">
                      <template v-for="(round, rIdx) in analysisData.structure">
                        <!-- Заголовок раунда -->
                        <tr :key="'round-header-' + rIdx" class="round-header-row">
                          <td :colspan="maxQuestionsInRound(round) + 2" class="round-header-cell">
                            {{ round.name }}
                          </td>
                        </tr>
                        <!-- Заголовок стоимостей -->
                        <tr :key="'round-prices-' + rIdx" class="prices-header-row">
                          <td class="theme-name-cell"></td>
                          <td
                              v-for="(price, pIdx) in roundPrices(round)"
                              :key="'price-' + rIdx + '-' + pIdx"
                              class="price-header-cell"
                          >
                            {{ price }}
                          </td>
                          <td class="price-header-cell summary-header-cell">Итого</td>
                        </tr>
                        <!-- Темы -->
                        <tr
                            v-for="(theme, tIdx) in round.themes"
                            :key="'theme-' + rIdx + '-' + tIdx"
                            class="theme-row"
                        >
                          <td class="theme-name-cell" :title="theme.name">{{ theme.name }}</td>
                          <td
                              v-for="(song, sIdx) in theme.songs"
                              :key="'song-' + rIdx + '-' + tIdx + '-' + sIdx"
                              class="song-cell"
                              :style="getSongCellStyle(song)"
                              :title="song.label || songLabel(song)"
                          >
                            <small>{{ song.artist }}</small>
                          </td>
                          <td class="summary-cell" v-if="theme.summary">
                            <div class="inline-bar" v-if="getSummarySegments(theme.summary).length">
                              <div
                                  v-for="seg in getSummarySegments(theme.summary)"
                                  :key="seg.label"
                                  class="bar-seg"
                                  :style="{width: seg.percent + '%', backgroundColor: seg.color}"
                                  @mouseenter="showBarTooltip($event, seg, 'count')"
                                  @mouseleave="hideBarTooltip"
                              ></div>
                            </div>
                            <div class="summary-counts">
                              <small>{{ theme.summary.total }} шт</small>
                            </div>
                            <div class="inline-bar mt-25" v-if="getSummaryCostSegments(theme.summary).length">
                              <div
                                  v-for="seg in getSummaryCostSegments(theme.summary)"
                                  :key="'cost-' + seg.label"
                                  class="bar-seg"
                                  :style="{width: seg.percent + '%', backgroundColor: seg.color}"
                                  @mouseenter="showBarTooltip($event, seg, 'cost')"
                                  @mouseleave="hideBarTooltip"
                              ></div>
                            </div>
                            <div class="summary-counts" v-if="theme.summary.total_cost != null">
                              <small class="text-muted">{{ theme.summary.total_cost }} очков</small>
                            </div>
                          </td>
                        </tr>
                        <!-- Подитог раунда -->
                        <tr :key="'round-summary-' + rIdx" class="round-summary-row" v-if="round.summary">
                          <td class="round-summary-label">Итого {{ round.name }}</td>
                          <td :colspan="maxQuestionsInRound(round)" class="round-summary-cell">
                            <div class="inline-bar inline-bar--round" v-if="getSummarySegments(round.summary).length">
                              <div
                                  v-for="seg in getSummarySegments(round.summary)"
                                  :key="seg.label"
                                  class="bar-seg"
                                  :style="{width: seg.percent + '%', backgroundColor: seg.color}"
                                  @mouseenter="showBarTooltip($event, seg, 'count')"
                                  @mouseleave="hideBarTooltip"
                              ></div>
                            </div>
                            <div class="inline-bar inline-bar--round mt-25" v-if="getSummaryCostSegments(round.summary).length">
                              <div
                                  v-for="seg in getSummaryCostSegments(round.summary)"
                                  :key="'cost-' + seg.label"
                                  class="bar-seg"
                                  :style="{width: seg.percent + '%', backgroundColor: seg.color}"
                                  @mouseenter="showBarTooltip($event, seg, 'cost')"
                                  @mouseleave="hideBarTooltip"
                              ></div>
                            </div>
                          </td>
                          <td class="round-summary-cell round-summary-total">
                            <strong>{{ round.summary.total }} шт</strong>
                            <div v-if="round.summary.total_cost != null" class="text-muted" style="font-size: 0.78rem">
                              {{ round.summary.total_cost }} очков
                            </div>
                          </td>
                        </tr>
                      </template>
                      <!-- Итого по пакету -->
                      <tr v-if="packSummary" class="pack-summary-row">
                        <td class="pack-summary-label">Итого по пакету</td>
                        <td :colspan="maxQuestionsInPack" class="pack-summary-cell">
                          <div class="inline-bar inline-bar--round" v-if="getSummarySegments(packSummary).length">
                            <div
                                v-for="seg in getSummarySegments(packSummary)"
                                :key="'pack-' + seg.label"
                                class="bar-seg"
                                :style="{width: seg.percent + '%', backgroundColor: seg.color}"
                                @mouseenter="showBarTooltip($event, seg, 'count')"
                                @mouseleave="hideBarTooltip"
                            ></div>
                          </div>
                          <div class="inline-bar inline-bar--round mt-25" v-if="getSummaryCostSegments(packSummary).length">
                            <div
                                v-for="seg in getSummaryCostSegments(packSummary)"
                                :key="'pack-cost-' + seg.label"
                                class="bar-seg"
                                :style="{width: seg.percent + '%', backgroundColor: seg.color}"
                                @mouseenter="showBarTooltip($event, seg, 'cost')"
                                @mouseleave="hideBarTooltip"
                            ></div>
                          </div>
                        </td>
                        <td class="pack-summary-cell pack-summary-total">
                          <strong>{{ packSummary.total }} шт</strong>
                          <div v-if="packSummary.total_cost != null" class="text-muted" style="font-size: 0.78rem">
                            {{ packSummary.total_cost }} очков
                          </div>
                        </td>
                      </tr>
                    </table>
                  </div>
                </b-tab>
              </b-tabs>

            </b-card>
          </b-overlay>
        </b-col>
      </b-row>

      <b-row v-else>
        <b-col cols="12">
          <b-overlay
              :show="isLoading"
              rounded="sm"
          >
            <b-card
                title="Данных нет"
                class="col-md-12 col-12"
            >
              <b-card-text>
                Загрузите SIQ-файл для анализа
              </b-card-text>
            </b-card>
          </b-overlay>
        </b-col>
      </b-row>
    </div>
    <!-- Тултип для инлайн-диаграмм -->
    <div
        v-if="barTooltip.visible"
        class="bar-tooltip"
        :style="{top: barTooltip.y + 'px', left: barTooltip.x + 'px'}"
    >
      {{ barTooltip.text }}
    </div>
  </div>
</template>

<script>
import {
  BFormGroup,
  BCard,
  BRow,
  BCol,
  BCardText,
  BButton,
  BButtonGroup,
  BOverlay,
  BTabs,
  BTab,
  BAlert,
  BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import store from "@/store";
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onUnmounted, ref, computed} from "@vue/composition-api";
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {useInputImageRenderer} from "@core/comp-functions/forms/form-utils";
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BCardText,
    BButton,
    BButtonGroup,
    BOverlay,
    BTabs,
    BTab,
    BAlert,
    BBadge,
    VueApexCharts,
  },
  directives: {
    Ripple,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const analysisData = ref(null)
    const isLoading = ref(false)
    const refInputUploadFile = ref(null)

    // Chart colors
    const chartColors = {
      russian: '#28c76f',
      foreign: '#7367f0',
      rock: '#ff9f43',
      other: '#00cfe8',
      century20: '#ea5455',
      century21: '#82868b',
    }

    // Фильтрация песен по категориям
    const russianSongs = computed(() => {
      if (!analysisData.value || !analysisData.value.songs) return []
      return analysisData.value.songs.filter(s => s.is_russian)
    })

    const foreignSongs = computed(() => {
      if (!analysisData.value || !analysisData.value.songs) return []
      return analysisData.value.songs.filter(s => s.is_russian === false)
    })

    // Повторы/похожие артисты внутри пакета
    const artistDuplicates = computed(() => {
      if (!analysisData.value || !analysisData.value.artist_duplicates) return []
      return analysisData.value.artist_duplicates
    })

    // Песни по жанрам
    const songsByGenre = computed(() => {
      if (!analysisData.value || !analysisData.value.songs) return {}
      const result = {}
      analysisData.value.songs.forEach(song => {
        const genre = song.genre || 'Неизвестно'
        if (!result[genre]) result[genre] = []
        result[genre].push(song)
      })
      return result
    })

    // Песни по десятилетиям
    const songsByDecade = computed(() => {
      if (!analysisData.value || !analysisData.value.songs) return {}
      const result = {}
      analysisData.value.songs.forEach(song => {
        const decade = song.decade || (song.year ? Math.floor(song.year / 10) * 10 : null)
        if (decade) {
          if (!result[decade]) {
            result[decade] = []
          }
          result[decade].push(song)
        }
      })
      return result
    })

    // Отсортированные ключи десятилетий
    const sortedDecades = computed(() => {
      return Object.keys(songsByDecade.value).sort((a, b) => parseInt(a) - parseInt(b))
    })

    // Русские / Иностранные исполнители
    const artistsCountrySeries = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary) return [0, 0]
      return [
        analysisData.value.pack_summary.russian_artists_count,
        analysisData.value.pack_summary.foreign_artists_count,
      ]
    })

    const artistsCountryChartOptions = computed(() => ({
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '14px',
        fontFamily: 'Montserrat',
      },
      colors: [chartColors.russian, chartColors.foreign],
      labels: ['Русские', 'Иностранные'],
      dataLabels: {
        enabled: true,
        formatter(val) {
          return `${parseInt(val)}%`
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: {
                fontSize: '1.5rem',
                fontFamily: 'Montserrat',
              },
              value: {
                fontSize: '1rem',
                fontFamily: 'Montserrat',
              },
              total: {
                show: true,
                fontSize: '1rem',
                label: 'Всего',
                formatter() {
                  if (!analysisData.value || !analysisData.value.pack_summary) return '0'
                  return analysisData.value.pack_summary.russian_artists_count +
                      analysisData.value.pack_summary.foreign_artists_count
                },
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: {
              height: 280,
            },
          },
        },
      ],
    }))

    // Жанры из analysis.genres
    const genresData = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary || !analysisData.value.pack_summary.genres) {
        return { labels: [], values: [] }
      }
      const genres = analysisData.value.pack_summary.genres
      const labels = Object.keys(genres)
      const values = Object.values(genres)
      return { labels, values }
    })

    const genreSeries = computed(() => genresData.value.values)

    const genreChartColors = [
      '#ff9f43', '#00cfe8', '#7367f0', '#ea5455', '#28c76f', '#82868b', '#a855f7', '#f97316',
      '#0ea5e9', '#84cc16',
    ]

    const genreChartOptions = computed(() => ({
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '14px',
        fontFamily: 'Montserrat',
      },
      colors: genresData.value.labels.map(label => genreColorMap.value[label]),
      labels: genresData.value.labels,
      dataLabels: {
        enabled: true,
        formatter(val) {
          return `${parseInt(val)}%`
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: {
                fontSize: '1.5rem',
                fontFamily: 'Montserrat',
              },
              value: {
                fontSize: '1rem',
                fontFamily: 'Montserrat',
              },
              total: {
                show: true,
                fontSize: '1rem',
                label: 'Всего',
                formatter() {
                  return genresData.value.values.reduce((sum, v) => sum + v, 0)
                },
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: {
              height: 280,
            },
          },
        },
      ],
    }))

    function getGenreColor(genre) {
      return genreColorMap.value[genre] || '#82868b'
    }

    // Жанры = кластеры артистов, их набор приходит с бэкенда — цвета как у пончика жанров
    const genreColorMap = computed(() => {
      const map = {}
      genresData.value.labels.forEach((label, idx) => {
        map[label] = genreChartColors[idx % genreChartColors.length]
      })
      if (!map['Неизвестно']) map['Неизвестно'] = '#bfbfbf'
      return map
    })

    const LLM_FIELD_LABELS = {is_russian: 'язык', genre: 'жанр', decade: 'десятилетие'}

    function llmFieldsLabel(song) {
      const sources = song.category_source || {}
      return Object.keys(LLM_FIELD_LABELS)
        .filter(field => sources[field] === 'llm')
        .map(field => LLM_FIELD_LABELS[field])
        .join(', ')
    }

    // «Артист - Песня», а без названия — только артист (без висящего « - »)
    function songLabel(song) {
      return song.title ? `${song.artist} - ${song.title}` : song.artist
    }

    // Данные по десятилетиям
    const decadesData = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary || !analysisData.value.pack_summary.decades) {
        return { labels: [], values: [] }
      }
      const decades = analysisData.value.pack_summary.decades
      const sortedKeys = Object.keys(decades).sort((a, b) => parseInt(a) - parseInt(b))
      return {
        labels: sortedKeys.map(d => `${d}-е`),
        values: sortedKeys.map(d => decades[d]),
      }
    })

    // 20 век / 21 век - собираем из десятилетий
    const centurySeries = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary || !analysisData.value.pack_summary.decades) {
        return [0, 0]
      }
      const decades = analysisData.value.pack_summary.decades
      let century20 = 0
      let century21 = 0
      for (const [decade, count] of Object.entries(decades)) {
        const year = parseInt(decade)
        if (year < 2000) {
          century20 += count
        } else {
          century21 += count
        }
      }
      return [century20, century21]
    })

    const centuryChartOptions = computed(() => ({
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '14px',
        fontFamily: 'Montserrat',
      },
      colors: [chartColors.century20, chartColors.century21],
      labels: ['XX век', 'XXI век'],
      dataLabels: {
        enabled: true,
        formatter(val) {
          return `${parseInt(val)}%`
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: {
                fontSize: '1.5rem',
                fontFamily: 'Montserrat',
              },
              value: {
                fontSize: '1rem',
                fontFamily: 'Montserrat',
              },
              total: {
                show: true,
                fontSize: '1rem',
                label: 'Всего',
                formatter() {
                  const series = centurySeries.value
                  return series[0] + series[1]
                },
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: {
              height: 280,
            },
          },
        },
      ],
    }))

    // График по десятилетиям
    const decadesSeries = computed(() => {
      const values = decadesData.value.values
      const total = values.reduce((sum, v) => sum + v, 0)
      const percentages = total > 0 ? values.map(v => Math.round((v / total) * 100)) : values.map(() => 0)
      return [
        {
          name: 'Количество',
          type: 'column',
          data: values,
        },
        {
          name: 'Процент',
          type: 'line',
          data: percentages,
        },
      ]
    })

    const decadesChartOptions = computed(() => ({
      chart: {
        toolbar: {
          show: false,
        },
      },
      stroke: {
        width: [0, 3],
        curve: 'smooth',
      },
      colors: ['#7367f0', '#ff9f43'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
        },
      },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0],
      },
      markers: {
        size: [0, 5],
        colors: ['#ff9f43'],
        strokeColors: '#fff',
        strokeWidth: 2,
      },
      xaxis: {
        categories: decadesData.value.labels,
        labels: {
          style: {
            fontSize: '12px',
            fontFamily: 'Montserrat',
          },
        },
      },
      yaxis: [
        {
          title: {
            text: 'Количество треков',
            style: {
              fontFamily: 'Montserrat',
            },
          },
        },
        {
          opposite: true,
          title: {
            text: 'Процент',
            style: {
              fontFamily: 'Montserrat',
            },
          },
          labels: {
            formatter(val) {
              return `${val}%`
            },
          },
          min: 0,
          max: 100,
        },
      ],
      fill: {
        opacity: 1,
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter(val, { seriesIndex }) {
            return seriesIndex === 0 ? `${val} треков` : `${val}%`
          },
        },
      },
      legend: {
        show: true,
        position: 'top',
        fontFamily: 'Montserrat',
      },
    }))

    // === Графики по стоимости ===

    // По стране (стоимость)
    const artistsCountryCostSeries = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary) return [0, 0]
      return [
        analysisData.value.pack_summary.russian_artists_cost || 0,
        analysisData.value.pack_summary.foreign_artists_cost || 0,
      ]
    })

    const artistsCountryCostChartOptions = computed(() => ({
      legend: { show: true, position: 'bottom', fontSize: '14px', fontFamily: 'Montserrat' },
      colors: [chartColors.russian, chartColors.foreign],
      labels: ['Русские', 'Иностранные'],
      dataLabels: { enabled: true, formatter(val) { return `${parseInt(val)}%` } },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: { fontSize: '1.5rem', fontFamily: 'Montserrat' },
              value: { fontSize: '1rem', fontFamily: 'Montserrat' },
              total: {
                show: true, fontSize: '1rem', label: 'Всего',
                formatter() {
                  if (!analysisData.value || !analysisData.value.pack_summary) return '0'
                  return (analysisData.value.pack_summary.russian_artists_cost || 0) +
                      (analysisData.value.pack_summary.foreign_artists_cost || 0)
                },
              },
            },
          },
        },
      },
      responsive: [{ breakpoint: 576, options: { chart: { height: 280 } } }],
    }))

    // По жанру (стоимость)
    const genresCostData = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary || !analysisData.value.pack_summary.genres_cost) {
        return { labels: [], values: [] }
      }
      const genres = analysisData.value.pack_summary.genres_cost
      return { labels: Object.keys(genres), values: Object.values(genres) }
    })

    const genreCostSeries = computed(() => genresCostData.value.values)

    const genreCostChartOptions = computed(() => ({
      legend: { show: true, position: 'bottom', fontSize: '14px', fontFamily: 'Montserrat' },
      colors: genresCostData.value.labels.map(label => genreColorMap.value[label] || '#bfbfbf'),
      labels: genresCostData.value.labels,
      dataLabels: { enabled: true, formatter(val) { return `${parseInt(val)}%` } },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: { fontSize: '1.5rem', fontFamily: 'Montserrat' },
              value: { fontSize: '1rem', fontFamily: 'Montserrat' },
              total: {
                show: true, fontSize: '1rem', label: 'Всего',
                formatter() { return genresCostData.value.values.reduce((sum, v) => sum + v, 0) },
              },
            },
          },
        },
      },
      responsive: [{ breakpoint: 576, options: { chart: { height: 280 } } }],
    }))

    // По эпохе (стоимость)
    const centuryCostSeries = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary || !analysisData.value.pack_summary.decades_cost) {
        return [0, 0]
      }
      const decades = analysisData.value.pack_summary.decades_cost
      let century20 = 0
      let century21 = 0
      for (const [decade, cost] of Object.entries(decades)) {
        if (parseInt(decade) < 2000) century20 += cost
        else century21 += cost
      }
      return [century20, century21]
    })

    const centuryCostChartOptions = computed(() => ({
      legend: { show: true, position: 'bottom', fontSize: '14px', fontFamily: 'Montserrat' },
      colors: [chartColors.century20, chartColors.century21],
      labels: ['XX век', 'XXI век'],
      dataLabels: { enabled: true, formatter(val) { return `${parseInt(val)}%` } },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: { fontSize: '1.5rem', fontFamily: 'Montserrat' },
              value: { fontSize: '1rem', fontFamily: 'Montserrat' },
              total: {
                show: true, fontSize: '1rem', label: 'Всего',
                formatter() {
                  const s = centuryCostSeries.value
                  return s[0] + s[1]
                },
              },
            },
          },
        },
      },
      responsive: [{ breakpoint: 576, options: { chart: { height: 280 } } }],
    }))

    // По десятилетиям (стоимость)
    const decadesCostData = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary || !analysisData.value.pack_summary.decades_cost) {
        return { labels: [], values: [] }
      }
      const decades = analysisData.value.pack_summary.decades_cost
      const sortedKeys = Object.keys(decades).sort((a, b) => parseInt(a) - parseInt(b))
      return {
        labels: sortedKeys.map(d => `${d}-е`),
        values: sortedKeys.map(d => decades[d]),
      }
    })

    const decadesCostSeries = computed(() => {
      const values = decadesCostData.value.values
      const total = values.reduce((sum, v) => sum + v, 0)
      const percentages = total > 0 ? values.map(v => Math.round((v / total) * 100)) : values.map(() => 0)
      return [
        { name: 'Стоимость', type: 'column', data: values },
        { name: 'Процент', type: 'line', data: percentages },
      ]
    })

    const decadesCostChartOptions = computed(() => ({
      chart: { toolbar: { show: false } },
      stroke: { width: [0, 3], curve: 'smooth' },
      colors: ['#7367f0', '#ff9f43'],
      plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 4 } },
      dataLabels: { enabled: true, enabledOnSeries: [0] },
      markers: { size: [0, 5], colors: ['#ff9f43'], strokeColors: '#fff', strokeWidth: 2 },
      xaxis: {
        categories: decadesCostData.value.labels,
        labels: { style: { fontSize: '12px', fontFamily: 'Montserrat' } },
      },
      yaxis: [
        { title: { text: 'Стоимость', style: { fontFamily: 'Montserrat' } } },
        {
          opposite: true,
          title: { text: 'Процент', style: { fontFamily: 'Montserrat' } },
          labels: { formatter(val) { return `${val}%` } },
          min: 0, max: 100,
        },
      ],
      fill: { opacity: 1 },
      tooltip: {
        shared: true, intersect: false,
        y: { formatter(val, { seriesIndex }) { return seriesIndex === 0 ? `${val} очков` : `${val}%` } },
      },
      legend: { show: true, position: 'top', fontFamily: 'Montserrat' },
    }))

    // === Структура пакета ===
    const structureColorMode = ref(null) // 'language' | 'genre' | 'decade' | null

    const structureColorMaps = {
      language: {
        'Русский': '#5b9bd5',
        'Иностранный': '#ed7d31',
        'Неизвестно': '#bfbfbf',
      },
      // Геттер читает computed, поэтому остаётся реактивным — не превращать в снимок значения
      get genre() {
        return genreColorMap.value
      },
      decade: {
        1950: '#8b5e3c',
        1960: '#c0504d',
        1970: '#f79646',
        1980: '#e6b800',
        1990: '#9bbb59',
        2000: '#4bacc6',
        2010: '#4f81bd',
        2020: '#8064a2',
      },
    }

    const currentLegend = computed(() => {
      if (!structureColorMode.value) return []
      const map = structureColorMaps[structureColorMode.value]
      if (!map) return []
      if (structureColorMode.value === 'decade') {
        // Показываем только декады, реально присутствующие в данных
        const allDecades = new Set()
        if (analysisData.value && analysisData.value.structure) {
          analysisData.value.structure.forEach(round => {
            round.themes.forEach(theme => {
              theme.songs.forEach(song => {
                if (song.decade) allDecades.add(song.decade)
              })
            })
          })
        }
        return Array.from(allDecades).sort((a, b) => a - b).map(d => ({
          label: `${d}-е`,
          color: map[d] || '#bfbfbf',
        }))
      }
      return Object.entries(map).map(([label, color]) => ({ label, color }))
    })

    function getSongCellStyle(song) {
      if (!structureColorMode.value) return {}
      let color = '#bfbfbf'
      if (structureColorMode.value === 'language') {
        if (song.is_russian === true) color = structureColorMaps.language['Русский']
        else if (song.is_russian === false) color = structureColorMaps.language['Иностранный']
        else color = structureColorMaps.language['Неизвестно']
      } else if (structureColorMode.value === 'genre') {
        const genre = song.genre || 'Неизвестно'
        color = structureColorMaps.genre[genre] || '#bfbfbf'
      } else if (structureColorMode.value === 'decade') {
        color = structureColorMaps.decade[song.decade] || '#bfbfbf'
      }
      return { backgroundColor: color, color: '#fff' }
    }

    function maxQuestionsInRound(round) {
      if (!round.themes || round.themes.length === 0) return 0
      return Math.max(...round.themes.map(t => t.songs ? t.songs.length : 0))
    }

    const barTooltip = ref({ visible: false, x: 0, y: 0, text: '' })

    function getSummarySegments(summary) {
      if (!summary) return []
      const mode = structureColorMode.value
      if (!mode) return []
      const segments = []
      if (mode === 'language') {
        if (summary.russian_artists_count) {
          segments.push({ label: 'Рус', count: summary.russian_artists_count, cost: summary.russian_artists_cost, color: structureColorMaps.language['Русский'] })
        }
        if (summary.foreign_artists_count) {
          segments.push({ label: 'Ин', count: summary.foreign_artists_count, cost: summary.foreign_artists_cost, color: structureColorMaps.language['Иностранный'] })
        }
      } else if (mode === 'genre' && summary.genres) {
        const costMap = summary.genres_cost || {}
        Object.entries(summary.genres).forEach(([g, c]) => {
          segments.push({ label: g, count: c, cost: costMap[g], color: structureColorMaps.genre[g] || '#bfbfbf' })
        })
      } else if (mode === 'decade' && summary.decades) {
        const costMap = summary.decades_cost || {}
        Object.entries(summary.decades)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .forEach(([d, c]) => {
            segments.push({ label: `${d}-е`, count: c, cost: costMap[d], color: structureColorMaps.decade[d] || '#bfbfbf' })
          })
      }
      const total = segments.reduce((s, seg) => s + seg.count, 0)
      if (total > 0) {
        segments.forEach(seg => {
          seg.percent = (seg.count / total) * 100
        })
      }
      return segments
    }

    function getSummaryCostSegments(summary) {
      if (!summary) return []
      const mode = structureColorMode.value
      if (!mode) return []
      const segments = []
      if (mode === 'language') {
        if (summary.russian_artists_cost) {
          segments.push({ label: 'Рус', cost: summary.russian_artists_cost, color: structureColorMaps.language['Русский'] })
        }
        if (summary.foreign_artists_cost) {
          segments.push({ label: 'Ин', cost: summary.foreign_artists_cost, color: structureColorMaps.language['Иностранный'] })
        }
      } else if (mode === 'genre' && summary.genres_cost) {
        Object.entries(summary.genres_cost).forEach(([g, c]) => {
          segments.push({ label: g, cost: c, color: structureColorMaps.genre[g] || '#bfbfbf' })
        })
      } else if (mode === 'decade' && summary.decades_cost) {
        Object.entries(summary.decades_cost)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .forEach(([d, c]) => {
            segments.push({ label: `${d}-е`, cost: c, color: structureColorMaps.decade[d] || '#bfbfbf' })
          })
      }
      const total = segments.reduce((s, seg) => s + seg.cost, 0)
      if (total > 0) {
        segments.forEach(seg => {
          seg.percent = (seg.cost / total) * 100
        })
      }
      return segments
    }

    function showBarTooltip(event, seg, type) {
      const rect = event.target.getBoundingClientRect()
      let text
      if (type === 'cost') {
        text = `${seg.label}: ${seg.cost} очков`
      } else {
        text = `${seg.label}: ${seg.count} шт`
      }
      barTooltip.value = {
        visible: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 28,
        text,
      }
    }

    function hideBarTooltip() {
      barTooltip.value = { visible: false, x: 0, y: 0, text: '' }
    }

    // Общий итог по пакету
    const packSummary = computed(() => {
      if (!analysisData.value || !analysisData.value.pack_summary) return null
      return analysisData.value.pack_summary
    })

    // Максимальное кол-во вопросов в теме по всем раундам (для colspan итоговой строки пакета)
    const maxQuestionsInPack = computed(() => {
      if (!analysisData.value || !analysisData.value.structure) return 0
      let max = 0
      analysisData.value.structure.forEach(round => {
        const m = maxQuestionsInRound(round)
        if (m > max) max = m
      })
      return max
    })

    function roundPrices(round) {
      if (!round.themes || round.themes.length === 0) return []
      const maxLen = maxQuestionsInRound(round)
      // Берём стоимости из первой темы, которая имеет полный набор вопросов
      const fullTheme = round.themes.find(t => t.songs && t.songs.length === maxLen)
      if (fullTheme) return fullTheme.songs.map(s => s.price)
      // Fallback: генерируем на основании количества
      return Array.from({ length: maxLen }, (_, i) => (i + 1) * 100)
    }

    const {inputImageRenderer: inputSiqFileRenderer} = useInputImageRenderer(refInputUploadFile, async base64 => {
      const file = refInputUploadFile.value.files[0]
      isLoading.value = true
      try {
        const res = await store.dispatch('playlist/sigameAnalyze', {file: file})
        analysisData.value = res.data
        if (res.data.llm_error) {
          toast({
            component: ToastificationContent,
            props: {
              title: 'ЛЛМ недоступна — часть песен без категорий',
              text: res.data.llm_error,
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки файла',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    })

    return {
      analysisData,
      isLoading,
      inputSiqFileRenderer,
      refInputUploadFile,
      llmFieldsLabel,
      songLabel,
      artistsCountrySeries,
      artistsCountryChartOptions,
      genreSeries,
      genreChartOptions,
      centurySeries,
      centuryChartOptions,
      decadesSeries,
      decadesChartOptions,
      // Графики по стоимости
      artistsCountryCostSeries,
      artistsCountryCostChartOptions,
      genreCostSeries,
      genreCostChartOptions,
      centuryCostSeries,
      centuryCostChartOptions,
      decadesCostSeries,
      decadesCostChartOptions,
      songsByDecade,
      sortedDecades,
      songsByGenre,
      getGenreColor,
      // Списки песен
      russianSongs,
      foreignSongs,
      artistDuplicates,
      // Структура
      structureColorMode,
      currentLegend,
      getSongCellStyle,
      maxQuestionsInRound,
      maxQuestionsInPack,
      packSummary,
      roundPrices,
      getSummarySegments,
      getSummaryCostSegments,
      barTooltip,
      showBarTooltip,
      hideBarTooltip,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';

.songs-lists {
  border-top: 1px solid #ebe9f1;
  padding-top: 1rem;
}

.songs-scroll {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 2px;
  }
}

.song-item {
  padding: 2px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

// Цвета для категорий
.text-russian {
  color: #28c76f;
}

.text-foreign {
  color: #7367f0;
}

.text-century20 {
  color: #ea5455;
}

.text-century21 {
  color: #82868b;
}

// Легенда структуры
.structure-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .legend-color {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.15);
  }

  .legend-label {
    font-size: 0.85rem;
  }
}

// Таблица структуры пакета
.structure-table-wrapper {
  overflow-x: auto;
}

.structure-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  .round-header-row .round-header-cell {
    background-color: #3b4253;
    color: #fff;
    font-weight: 600;
    font-size: 1rem;
    padding: 0.6rem 0.75rem;
    text-align: center;
    border: 1px solid #3b4253;
  }

  .prices-header-row {
    td {
      background-color: #f8f8f8;
      font-weight: 600;
      text-align: center;
      padding: 0.35rem 0.5rem;
      border: 1px solid #ebe9f1;
      color: #5e5873;
    }
  }

  .theme-row {
    .theme-name-cell {
      min-width: 180px;
      max-width: 250px;
      padding: 0.4rem 0.6rem;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      border: 1px solid #ebe9f1;
      background-color: #fafafa;
    }

    .song-cell {
      min-width: 100px;
      max-width: 160px;
      padding: 0.35rem 0.5rem;
      text-align: center;
      border: 1px solid #ebe9f1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: background-color 0.2s ease;

      small {
        font-size: 0.78rem;
      }
    }

    .summary-cell {
      min-width: 120px;
      padding: 0.35rem 0.5rem;
      text-align: center;
      border: 1px solid #ebe9f1;
      background-color: #f4f4f4;
      white-space: nowrap;

      small {
        font-size: 0.75rem;
      }

      .summary-counts {
        margin-top: 2px;
        line-height: 1.2;
      }
    }

    .summary-header-cell {
      font-style: italic;
    }
  }

  .round-summary-row {
    .round-summary-label {
      padding: 0.5rem 0.6rem;
      font-weight: 600;
      font-size: 0.85rem;
      background-color: #eeedfd;
      border: 1px solid #ebe9f1;
      white-space: nowrap;
    }

    .round-summary-cell {
      padding: 0.5rem 0.6rem;
      background-color: #eeedfd;
      border: 1px solid #ebe9f1;
      font-size: 0.82rem;
    }

    .round-summary-total {
      text-align: center;
      font-size: 0.9rem;
    }
  }

  .pack-summary-row {
    .pack-summary-label {
      padding: 0.6rem 0.75rem;
      font-weight: 700;
      font-size: 0.9rem;
      background-color: #3b4253;
      color: #fff;
      border: 1px solid #3b4253;
      white-space: nowrap;
    }

    .pack-summary-cell {
      padding: 0.6rem 0.75rem;
      background-color: #e8e6fd;
      border: 1px solid #ebe9f1;
      font-size: 0.85rem;
    }

    .pack-summary-total {
      text-align: center;
      font-size: 0.95rem;
    }
  }
}

// Инлайн-диаграммы
.inline-bar {
  display: flex;
  height: 14px;
  border-radius: 3px;
  overflow: hidden;
  min-width: 60px;

  &--round {
    height: 18px;
  }

  .bar-seg {
    height: 100%;
    min-width: 2px;
    transition: opacity 0.15s ease;
    cursor: default;

    &:hover {
      opacity: 0.75;
    }
  }
}

.bar-tooltip {
  position: fixed;
  background: #3b4253;
  color: #fff;
  font-size: 0.72rem;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  transform: translateX(-50%);
  z-index: 1050;
}

// Dark mode
.dark-layout {
  .structure-table {
    .prices-header-row td {
      background-color: #283046;
      color: #b4b7bd;
      border-color: #3b4253;
    }

    .theme-row {
      .theme-name-cell {
        background-color: #283046;
        color: #b4b7bd;
        border-color: #3b4253;
      }

      .song-cell {
        border-color: #3b4253;
      }

      .summary-cell {
        background-color: #242b3d;
        border-color: #3b4253;
      }
    }

    .round-summary-row {
      .round-summary-label,
      .round-summary-cell {
        background-color: #1e2538;
        border-color: #3b4253;
        color: #b4b7bd;
      }
    }

    .pack-summary-row {
      .pack-summary-cell {
        background-color: #1a2035;
        border-color: #3b4253;
        color: #b4b7bd;
      }
    }
  }
}
</style>
